import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Plus, X, Edit2, Check } from 'lucide-react';

const SlotTemplateManager = ({ onSlotsChange }) => {
    const [templates, setTemplates] = useState([
        { id: '1', start_time: '09:00', end_time: '10:00', selected: false, custom: false },
        { id: '2', start_time: '10:00', end_time: '11:00', selected: false, custom: false },
        { id: '3', start_time: '11:00', end_time: '12:00', selected: false, custom: false },
        { id: '4', start_time: '12:00', end_time: '13:00', selected: false, custom: false },
        { id: '5', start_time: '13:00', end_time: '14:00', selected: false, custom: false },
        { id: '6', start_time: '14:00', end_time: '15:00', selected: false, custom: false },
        { id: '7', start_time: '15:00', end_time: '16:00', selected: false, custom: false },
        { id: '8', start_time: '16:00', end_time: '17:00', selected: false, custom: false },
        { id: '9', start_time: '17:00', end_time: '18:00', selected: false, custom: false },
        { id: '10', start_time: '18:00', end_time: '19:00', selected: false, custom: false },
    ]);

    const [customSlot, setCustomSlot] = useState({ start_time: '', end_time: '' });
    const [editingId, setEditingId] = useState(null);
    const [editingSlot, setEditingSlot] = useState({ start_time: '', end_time: '' });

    const toggleSlot = (id) => {
        const updatedTemplates = templates.map(t =>
            t.id === id ? { ...t, selected: !t.selected } : t
        );
        setTemplates(updatedTemplates);
        notifyParent(updatedTemplates);
    };

    const startEditing = (template) => {
        setEditingId(template.id);
        setEditingSlot({ start_time: template.start_time, end_time: template.end_time });
    };

    const saveEdit = () => {
        const updatedTemplates = templates.map(t =>
            t.id === editingId ? { ...t, start_time: editingSlot.start_time, end_time: editingSlot.end_time } : t
        );
        setTemplates(updatedTemplates);
        setEditingId(null);
        notifyParent(updatedTemplates);
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditingSlot({ start_time: '', end_time: '' });
    };

    const addCustomSlot = () => {
        if (!customSlot.start_time || !customSlot.end_time) {
            return;
        }

        const newSlot = {
            id: `custom-${Date.now()}`,
            start_time: customSlot.start_time,
            end_time: customSlot.end_time,
            selected: true,
            custom: true
        };

        const updatedTemplates = [...templates, newSlot];
        setTemplates(updatedTemplates);
        setCustomSlot({ start_time: '', end_time: '' });
        notifyParent(updatedTemplates);
    };

    const removeCustomSlot = (id) => {
        const updatedTemplates = templates.filter(t => t.id !== id);
        setTemplates(updatedTemplates);
        notifyParent(updatedTemplates);
    };

    const selectAll = () => {
        const updatedTemplates = templates.map(t => ({ ...t, selected: true }));
        setTemplates(updatedTemplates);
        notifyParent(updatedTemplates);
    };

    const deselectAll = () => {
        const updatedTemplates = templates.map(t => ({ ...t, selected: false }));
        setTemplates(updatedTemplates);
        notifyParent(updatedTemplates);
    };

    const notifyParent = (updatedTemplates) => {
        const selectedSlots = updatedTemplates
            .filter(t => t.selected)
            .map(t => ({ start_time: t.start_time, end_time: t.end_time }));
        onSlotsChange(selectedSlots);
    };

    const selectedCount = templates.filter(t => t.selected).length;

    return (
        <Card className="admin-card">
            <CardHeader>
                <div className="flex justify-between items-center">
                    <CardTitle style={{ color: 'var(--admin-text-primary)' }}>
                        Шаблоны временных слотов
                    </CardTitle>
                    <div className="flex gap-2">
                        <Button onClick={selectAll} variant="outline" size="sm">
                            Выбрать все
                        </Button>
                        <Button onClick={deselectAll} variant="outline" size="sm">
                            Снять все
                        </Button>
                    </div>
                </div>
                <p className="text-sm mt-2" style={{ color: 'var(--admin-text-secondary)' }}>
                    Выбрано: <strong>{selectedCount}</strong> {selectedCount === 1 ? 'слот' : 'слотов'}
                </p>
            </CardHeader>
            <CardContent>
                {/* Predefined Templates */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 mb-4">
                    {templates.filter(t => !t.custom).map(template => (
                        <div key={template.id}>
                            {editingId === template.id ? (
                                <div className="p-2 rounded" style={{ background: 'var(--admin-bg-secondary)', border: '1px solid var(--admin-border)' }}>
                                    <div className="flex gap-1 mb-1">
                                        <Input
                                            type="time"
                                            value={editingSlot.start_time}
                                            onChange={(e) => setEditingSlot({ ...editingSlot, start_time: e.target.value })}
                                            className="admin-input text-xs p-1 h-7"
                                        />
                                        <Input
                                            type="time"
                                            value={editingSlot.end_time}
                                            onChange={(e) => setEditingSlot({ ...editingSlot, end_time: e.target.value })}
                                            className="admin-input text-xs p-1 h-7"
                                        />
                                    </div>
                                    <div className="flex gap-1">
                                        <Button size="sm" onClick={saveEdit} className="flex-1 h-6 text-xs">
                                            <Check size={12} />
                                        </Button>
                                        <Button size="sm" variant="outline" onClick={cancelEdit} className="flex-1 h-6 text-xs">
                                            <X size={12} />
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <label
                                    className="flex items-center justify-between gap-2 p-3 rounded cursor-pointer transition-colors group"
                                    style={{
                                        background: template.selected ? 'var(--admin-button-primary-bg)' : 'var(--admin-bg-secondary)',
                                        color: template.selected ? 'var(--admin-button-primary-text)' : 'var(--admin-text-primary)',
                                        border: `1px solid ${template.selected ? 'var(--admin-button-primary-bg)' : 'var(--admin-border)'}`
                                    }}
                                >
                                    <div className="flex items-center gap-2 flex-1">
                                        <input
                                            type="checkbox"
                                            checked={template.selected}
                                            onChange={() => toggleSlot(template.id)}
                                            className="w-4 h-4"
                                        />
                                        <span className="text-sm font-medium">
                                            {template.start_time} - {template.end_time}
                                        </span>
                                    </div>
                                    <button
                                        onClick={(e) => {
                                            e.preventDefault();
                                            startEditing(template);
                                        }}
                                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1"
                                        style={{ color: template.selected ? 'var(--admin-button-primary-text)' : 'var(--admin-text-secondary)' }}
                                    >
                                        <Edit2 size={14} />
                                    </button>
                                </label>
                            )}
                        </div>
                    ))}
                </div>

                {/* Custom Slots */}
                {templates.filter(t => t.custom).length > 0 && (
                    <div className="mb-4">
                        <h4 className="text-sm font-medium mb-2" style={{ color: 'var(--admin-text-primary)' }}>
                            Кастомные слоты:
                        </h4>
                        <div className="space-y-2">
                            {templates.filter(t => t.custom).map(template => (
                                <div
                                    key={template.id}
                                    className="flex items-center gap-2 p-2 rounded"
                                    style={{ background: 'var(--admin-bg-secondary)' }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={template.selected}
                                        onChange={() => toggleSlot(template.id)}
                                        className="w-4 h-4"
                                    />
                                    <span className="flex-1 text-sm">
                                        {template.start_time} - {template.end_time}
                                    </span>
                                    <button
                                        onClick={() => removeCustomSlot(template.id)}
                                        className="p-1 rounded hover:bg-red-100"
                                        style={{ color: 'var(--admin-error)' }}
                                    >
                                        <X size={16} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Add Custom Slot */}
                <div className="border-t pt-4" style={{ borderColor: 'var(--admin-border)' }}>
                    <h4 className="text-sm font-medium mb-2" style={{ color: 'var(--admin-text-primary)' }}>
                        Добавить кастомный слот:
                    </h4>
                    <div className="flex gap-2">
                        <Input
                            type="time"
                            value={customSlot.start_time}
                            onChange={(e) => setCustomSlot({ ...customSlot, start_time: e.target.value })}
                            placeholder="Начало"
                            className="admin-input"
                        />
                        <Input
                            type="time"
                            value={customSlot.end_time}
                            onChange={(e) => setCustomSlot({ ...customSlot, end_time: e.target.value })}
                            placeholder="Конец"
                            className="admin-input"
                        />
                        <Button onClick={addCustomSlot} variant="outline" size="sm">
                            <Plus size={18} />
                        </Button>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

export default SlotTemplateManager;
